import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft } from 'lucide-react';
// import type { Ambiente, Volume, Regiao } from '@prisma/client'; // Removido temporariamente
import { useToast } from '../../contexts/ToastContext';
import api from '../../services/api'; // Importar api

// Enum temporário, idealmente viria do Prisma
enum RegiaoEnum { 
  br_se1 = 'br_se1',
  br_mgl1 = 'br_mgl1',
  br_se2 = 'br_se2',
}

// Schema de validação com Zod
const schema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  tamanho: z.number({
    required_error: "Tamanho é obrigatório",
    invalid_type_error: "Tamanho deve ser um número",
  }).int().positive('Tamanho deve ser um número positivo'),
  ambiente_id: z.string().min(1, 'Ambiente é obrigatório'),
  regiao: z.nativeEnum(RegiaoEnum, { errorMap: () => ({ message: 'Região é obrigatória' }) }), // Usar enum local
  snapshot_time: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Hora inválida (HH:MM)").optional().or(z.literal('')),
  weekdays: z.array(z.string()).optional(),
  ativo: z.boolean().default(true),
  snapshot_retention_limit: z
    .union([
      z.literal(''),
      z.coerce.number().int('Deve ser um número inteiro').min(1, 'Mínimo 1'),
    ])
    .optional(),
});

type FormData = z.infer<typeof schema>;
type Ambiente = any; // Tipo temporário
type Volume = any;   // Tipo temporário

// Opções de Região
const regioesOptions = Object.values(RegiaoEnum).map(value => ({
  value,
  label: value.replace('_', '-').toUpperCase()
}));


// Opções de Dias da Semana
const weekdayOptions = [
  { value: 'mon', label: 'Segunda' },
  { value: 'tue', label: 'Terça' },
  { value: 'wed', label: 'Quarta' },
  { value: 'thu', label: 'Quinta' },
  { value: 'fri', label: 'Sexta' },
  { value: 'sat', label: 'Sábado' },
  { value: 'sun', label: 'Domingo' },
];

// Função auxiliar para formatar datas
const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch (error) {
    console.error("Erro ao formatar data:", dateString, error);
    return 'Data inválida';
  }
};

export const VolumeForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const [ambientes, setAmbientes] = useState<Ambiente[]>([]);
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [volumeData, setVolumeData] = useState<Volume | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      ativo: true,
      weekdays: [],
      snapshot_time: '',
      snapshot_retention_limit: '',
      tamanho: 10, 
    },
  });

  const selectedWeekdays = watch('weekdays') || [];

  useEffect(() => {
    fetchAmbientes();
    if (isEditing && id) {
      fetchVolume(id);
    } else {
      setTimeout(() => {
        document.getElementById('nome')?.focus();
      }, 100);
    }
  }, [id, isEditing]);

  const fetchAmbientes = async () => {
    try {
      // Usar api.get
      const response = await api.get<Ambiente[]>('/api/ambientes');
      setAmbientes(response.data);
    } catch (error: any) {
      console.error('Error fetching ambientes:', error);
      addToast({ type: 'error', title: 'Erro ao carregar ambientes', description: error.response?.data?.error || error.message });
    }
  };

  const fetchVolume = async (volumeId: string) => {
    setIsLoading(true);
    try {
      // Usar api.get
      const response = await api.get<Volume>(`/api/volumes/${volumeId}`);
      const data = response.data;
      if (!data) throw new Error('Volume não encontrado ou resposta inválida');
      reset({
        ...data,
        tamanho: typeof data.tamanho === 'string' ? parseInt(data.tamanho, 10) : data.tamanho,
        snapshot_time: data.snapshot_time || '',
        snapshot_retention_limit: data.snapshot_retention_limit ?? '',
      });
      setVolumeData(data);
    } catch (error: any) {
      console.error('Error fetching volume:', error);
      addToast({ type: 'error', title: 'Erro ao carregar volume', description: error.response?.data?.error || error.message });
      navigate('/volumes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWeekdayToggle = (day: string) => {
    const currentWeekdays = watch('weekdays') || [];
    const newWeekdays = currentWeekdays.includes(day)
      ? currentWeekdays.filter(d => d !== day)
      : [...currentWeekdays, day];
    setValue('weekdays', newWeekdays, { shouldValidate: true });
  };

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    const url = isEditing ? `/api/volumes/${id}` : '/api/volumes';
    const method = isEditing ? 'put' : 'post'; // Axios usa métodos em minúsculo
    
    // Preparar payload: remover campos não editáveis se estiver editando
    const retentionLimit =
      data.snapshot_retention_limit === '' || data.snapshot_retention_limit === undefined
        ? null
        : Number(data.snapshot_retention_limit);

    const payload = isEditing 
      ? { 
          snapshot_time: data.snapshot_time || null,
          weekdays: data.weekdays,
          ativo: data.ativo,
          snapshot_retention_limit: retentionLimit,
        }
      : { 
          ...data,
          tamanho: Number(data.tamanho),
          snapshot_time: data.snapshot_time || null,
          snapshot_retention_limit: retentionLimit,
        };

    try {
      let response;
      if (method === 'put') {
        response = await api.put(url, payload);
      } else {
        response = await api.post(url, payload);
      }

      // Verificar status 200 (OK) ou 201 (Created)
      if (response.status === 200 || response.status === 201) {
        addToast({
          type: 'success',
          title: isEditing ? 'Volume atualizado!' : 'Volume criado!',
        });
        navigate('/volumes');
      } else {
        // Se a API retornar um status diferente de 200/201, tratar como erro
        console.error('Falha ao salvar volume, status:', response.status, response.data);
        const errorDesc = response.data?.error || `Status: ${response.status}`;
         addToast({ type: 'error', title: 'Erro ao salvar', description: errorDesc });
        throw new Error(errorDesc || 'Falha ao salvar volume');
      }
    } catch (error: any) {
      console.error('Error saving volume:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido ao salvar.';
      addToast({ type: 'error', title: 'Erro ao salvar', description: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && isEditing) {
     return <div className="text-center p-4">Carregando dados do volume...</div>;
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/volumes')}
          className="mr-4 text-gray-600 hover:text-gray-900"
          aria-label="Voltar para a lista de volumes"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold">
          {isEditing ? 'Editar Volume' : 'Novo Volume'}
        </h1>
      </div>

      {isEditing && volumeData && (
         <div className="mb-6 p-4 bg-gray-100 rounded-lg text-sm text-gray-700 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div><strong>ID Volume (DB):</strong> {volumeData.id}</div>
            <div><strong>ID Magalu:</strong> {volumeData.magalu_id}</div>
            <div><strong>Status Magalu:</strong> {volumeData.status_magalu}</div>
            <div><strong>Nome:</strong> {volumeData.nome}</div>
            <div><strong>Ambiente:</strong> {volumeData.ambiente?.nome || '-'}</div>
            <div><strong>Região:</strong> {volumeData.regiao?.replace('_', '-').toUpperCase() || '-'}</div>
            <div><strong>Tamanho (GB):</strong> {volumeData.tamanho}</div>
            <div title={`ID Magalu VM: ${volumeData.magalu_vm_id || 'N/A'}`}> 
              <strong>Anexado à VM:</strong> {volumeData.vmNome || volumeData.magalu_vm_id || '-'}
            </div>
            <div><strong>Criado em:</strong> {formatDate(volumeData.createdAt)}</div>
            <div><strong>Atualizado em:</strong> {formatDate(volumeData.updatedAt)}</div>
         </div>
      )}


      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-white p-6 rounded-lg shadow">
        {!isEditing && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">
                Nome <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="nome"
                {...register('nome')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                disabled={isLoading || isSubmitting}
              />
              {errors.nome && <p className="mt-1 text-sm text-red-600">{errors.nome.message}</p>}
            </div>
            <div>
              <label htmlFor="tamanho" className="block text-sm font-medium text-gray-700 mb-1">
                Tamanho (GB) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="tamanho"
                {...register('tamanho', { valueAsNumber: true })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                disabled={isLoading || isSubmitting}
              />
              {errors.tamanho && <p className="mt-1 text-sm text-red-600">{errors.tamanho.message}</p>}
            </div>
          </div>
        )}

        {!isEditing && (
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div>
               <label htmlFor="ambiente_id" className="block text-sm font-medium text-gray-700 mb-1">
                 Ambiente <span className="text-red-500">*</span>
               </label>
               <select
                 id="ambiente_id"
                 {...register('ambiente_id')}
                 className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                 disabled={isLoading || isSubmitting}
               >
                 <option value="">Selecione...</option>
                 {ambientes.map((ambiente) => (
                   <option key={ambiente.id} value={ambiente.id}>
                     {ambiente.nome}
                   </option>
                 ))}
               </select>
               {errors.ambiente_id && <p className="mt-1 text-sm text-red-600">{errors.ambiente_id.message}</p>}
             </div>
             <div>
               <label htmlFor="regiao" className="block text-sm font-medium text-gray-700 mb-1">
                 Região <span className="text-red-500">*</span>
               </label>
               <select
                 id="regiao"
                 {...register('regiao')}
                 className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                 disabled={isLoading || isSubmitting}
               >
                 <option value="">Selecione...</option>
                 {regioesOptions.map((regiao) => (
                   <option key={regiao.value} value={regiao.value}>
                     {regiao.label}
                   </option>
                 ))}
               </select>
               {errors.regiao && <p className="mt-1 text-sm text-red-600">{errors.regiao.message}</p>}
             </div>
           </div>
        )}

        <fieldset className="border p-4 rounded-md">
          <legend className="text-sm font-medium text-gray-700 px-2">Agendamento de Snapshot</legend>
          <div className="mb-4 pt-2 max-w-xs">
            <label htmlFor="snapshot_retention_limit" className="block text-sm font-medium text-gray-700 mb-1">
              Retenção de snapshots (quantidade)
            </label>
            <input
              type="number"
              id="snapshot_retention_limit"
              min={1}
              placeholder="Sem limite"
              {...register('snapshot_retention_limit')}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              disabled={isLoading || isSubmitting}
            />
            <p className="mt-1 text-xs text-gray-500">
              Opcional. Apenas snapshots do agendamento deste sistema. Limpeza após o sync horário.
            </p>
            {errors.snapshot_retention_limit && (
              <p className="mt-1 text-sm text-red-600">{errors.snapshot_retention_limit.message}</p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start pt-2">
             <div className="flex items-center h-full mt-1 md:mt-0">
                <input
                  id="ativo"
                  type="checkbox"
                  {...register('ativo')}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={isLoading || isSubmitting}
                />
                <label htmlFor="ativo" className="ml-2 block text-sm text-gray-900">
                  Ativo
                </label>
             </div>
             <div>
                <label htmlFor="snapshot_time" className="block text-sm font-medium text-gray-700 mb-1">
                  Hora (HH:MM)
                </label>
                <input
                  type="time"
                  id="snapshot_time"
                  {...register('snapshot_time')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  disabled={isLoading || isSubmitting || !watch('ativo')}
                />
                {errors.snapshot_time && <p className="mt-1 text-sm text-red-600">{errors.snapshot_time.message}</p>}
             </div>
             <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">Dias da Semana</label>
                 <div className="space-y-2">
                    {weekdayOptions.map((day) => (
                      <div key={day.value} className="flex items-center">
                        <input
                          id={`weekday-${day.value}`}
                          type="checkbox"
                          value={day.value}
                          checked={selectedWeekdays.includes(day.value)}
                          onChange={() => handleWeekdayToggle(day.value)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                           disabled={isLoading || isSubmitting || !watch('ativo')}
                        />
                        <label htmlFor={`weekday-${day.value}`} className="ml-2 block text-sm text-gray-900">
                          {day.label}
                        </label>
                      </div>
                    ))}
                 </div>
             </div>
          </div>
        </fieldset>


        <div className="flex justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={() => navigate('/volumes')}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
             disabled={isLoading || isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading || isSubmitting}
          >
            {isSubmitting ? 'Salvando...' : 'Salvar Volume'}
          </button>
        </div>
      </form>
    </div>
  );
}; 